import dynamic from 'next/dynamic';

import { prefetchModule } from '@/utils/prefetch-module';

import type { SignUpModalProps } from './sign-up-modal';
import { SignUpModalSkeleton } from './sign-up-modal-skeleton';

const loadSignUpModal = () => import('./sign-up-modal');

// `error ? null` is mandatory. On a post-deploy chunk 404 the fallback would stay mounted forever, and it
// is a `fixed inset-0` overlay with no close button and no Escape handler (Escape lives inside the real
// modal), so it would trap the page. Returning null degrades to today's behaviour instead: nothing opens
// and the page stays usable.
//
// No `pastDelay` gate on purpose: the click that mounts this closes the sign-in modal, so the feedback has
// to land on the first frame rather than after the loadable's 200ms delay.
const SignUpModalWithSkeleton = dynamic(loadSignUpModal, { loading: ({ error }) => (error ? null : <SignUpModalSkeleton />) });

/**
 * The sign-up modal for the two call sites that load it on demand (the header and the sign-in modal).
 *
 * Wraps `animateOnMount={false}` rather than leaving it to each call site: the invariant is "a skeleton
 * painted this panel, so the panel must not fade in", and it belongs to the lazy path itself, not to
 * whoever happens to render it. The three static importers keep the default and their entrance fade.
 */
export function LazySignUpModal(props: Omit<SignUpModalProps, 'animateOnMount'>) {
  return <SignUpModalWithSkeleton {...props} animateOnMount={false} />;
}

/** Warms the sign-up chunk from a gesture that precedes the click (pointer enter, keyboard focus). */
export function prefetchSignUpModal(): void {
  prefetchModule(loadSignUpModal);
}
